import { api } from "@/utils/api";
import { SVGUploadImage } from "../../assets/register/svgs";
import { toast } from "@/utils/appToast";
import { useTranslation } from 'react-i18next';
import { updateUser } from "@/features/user/userSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import type { GoogleLocation } from "@/types/googleLocationTypes";
import type { Service, User, WorkerProfile } from "../../../types/userTypes";
import { AvatarPlaceholder } from "@/components/candidates/AvatarPlaceholder";
import { useNavigate } from "react-router-dom";
import Resizer from "react-image-file-resizer";

type StepProps = {
  handleChangeStep?: (back?: boolean) => void;
  workerProfile?: WorkerProfile;
};

export function UpdateWorkerStep1({
  handleChangeStep,
  workerProfile,
}: StepProps) {
  // const [address, setAddress] = useState({
  //   line1: "",
  //   line2: "",
  //   zipCode: "",
  // });
  const { t } = useTranslation();
  const [physicalAddress, setPhysicalAddress] = useState({ line1: '', line2: '', city: '', zipCode: '' });
  const [postalAddress, setPostalAddress] = useState('');
  const [googleLocation, setGoogleLocation] = useState<GoogleLocation>();
  const [imgPreview, setImgPreview] = useState<string>("");
  const [user, setUser] = useState<User>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [workerProfiles, setWorkerProfile] = useState<WorkerProfile>();
  // @ts-expect-error: Error de tipado en import.meta (no lo encuentra)
  const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    if (workerProfiles?.physicalAddress) {
      setPhysicalAddress({
        line1: workerProfiles.physicalAddress.line1 || '',
        line2: workerProfiles.physicalAddress.line2 || '',
        city: workerProfiles.physicalAddress.city || '',
        zipCode: workerProfiles.physicalAddress.zipCode || ''
      });
    }

    if (workerProfiles?.postalAddress) {
      setPostalAddress(workerProfiles.postalAddress);
    }
  }, [workerProfiles]);

  useEffect(() => {
    const addDatatoForm = (user: User) => {
      setWorkerProfile(user?.workerProfile);
      // setAddress(user?.workerProfile?.address);
      // Initialize selected skills from user profile
      setSelectedSkills(user?.workerProfile?.skills || []);
    };

    const fetchInitialData = async () => {
      const { services } = await api.getServices();
      setServices(services);
      const user = await api.getMe();
      if (user) {
        addDatatoForm(user);
        setUser(user);
        setImgPreview(user?.avatar);
        const locationbody = {
          label: user?.workerProfile?.locationName,
          value: {
            place_id: user?.workerProfile?.location?.value?.place_id,
            terms: user?.workerProfile?.location?.value?.terms,
          },
          coordinates: user?.workerProfile?.location?.coordinates,
        };
        setGoogleLocation(locationbody);
      }
    };
    fetchInitialData();
  }, []);

  const handleImgPreview = (e) => {
    const file = e.target.files[0];

    if (file) {


      if (file.size > 10 * 1024 * 1024) {
        toast.error("The profile photo cannot exceed 10MB");
        return;
      }


      const reader = new FileReader();
      reader.onload = () => {
        setImgPreview(reader.result as string);
      };

      reader.readAsDataURL(file);
    }
  };

  const resizeImage = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("The profile photo cannot exceed 10MB");
      return;
    }

    return new Promise<Blob>((resolve) => {
      Resizer.imageFileResizer(
        file,
        1500,
        1500,
        'JPEG',
        90,
        0,
        (uri: any) => {
          resolve(uri);
        },
        'blob'
      );
    });
  };

  const handleSkillToggle = (skillId: string) => {
    setSelectedSkills(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    const isNumber = /^[0-9]$/.test(e.key);
    const isAllowedKey = allowedKeys.includes(e.key);

    if (!isNumber && !isAllowedKey) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    
      const formData = new FormData(e.target as HTMLFormElement);
      const avatarImg = formData.get("avatar") as File;
      const hourlyPay = formData.get("hourlyPay") as string;
      // const maxDistance = formData.get("maxDistance") as string;

      // Validate hourly pay and max distance
      if (hourlyPay && (parseFloat(hourlyPay) <= 0 || !Number.isInteger(parseFloat(hourlyPay)))) {
        toast.error("El valor por hora debe ser un número entero positivo");
        setIsLoading(false);
        return;
      }

      if (hourlyPay && parseFloat(hourlyPay) > 1000) {
        toast.error("El valor por hora no puede exceder $1000");
        setIsLoading(false);
        return;
      }

      // if (selectedSkills.length === 0) {
      //   toast.error("Debe seleccionar al menos una habilidad");
      //   setIsLoading(false);
      //   return;
      // }

      if (!googleLocation) {
        console.log("googleLocation", googleLocation)
        toast.error("¡Debe proporcionar una ubicación válida!");
        setIsLoading(false);
        return;
      }

      // Gets GeoCode from selected Location
      const [locationGeoCode] = await geocodeByPlaceId(
        googleLocation.value.place_id
      ).catch((error) => {
        toast.error("Error al obtener la ubicación. Intente nuevamente.");
        setIsLoading(false);
        throw error;
      });

      // Gets Longitude and Latitude
      const locationLat = locationGeoCode.geometry.location.lat();
      const locationLong = locationGeoCode.geometry.location.lng();
      // Parse Location Coordinates
      const locationCoordinates = [locationLong, locationLat];
      if (avatarImg && avatarImg.size > 10 * 1024 * 1024) {
        toast.error("The profile photo cannot exceed 10MB");
        setIsLoading(false);
        return;
      }
      if (avatarImg.size) {
        // try {
        const resizedImage = await resizeImage(avatarImg);
        const avatarFormData = new FormData();
        avatarFormData.set("file", resizedImage);
        const userWithAvatarImg = await api.updateAvatarPhoto(avatarFormData);
        dispatch(updateUser({ ...userWithAvatarImg }));
        // } catch (error) {
        // console.error("Error updating avatar:", error);
        // toast.error("Error al actualizar la foto de perfil");
        // Continue with profile update even if avatar fails
        // }
      }

      const userData = {
        ...Object.fromEntries(formData),
        skills: selectedSkills,
        physicalAddress: {
          ...physicalAddress,
          city: googleLocation?.label || physicalAddress.city,
        },
        postalAddress,
        // address,
        location: {
          type: "Point",
          coordinates: locationCoordinates,
          value: {
            place_id: googleLocation.value.place_id,
            terms: googleLocation.value.terms,
          },
        },
        locationName: googleLocation.value.terms
          .slice(0, 2)
          .map((term) => term.value)
          .join(", "),
        serviceOffered: user?.workerProfile?.servicesOffered || [] // Fixed property name (was servicesOffered)
      };

      try {
        await api.updateMyWorkerProfile(userData);
        toast.success("Profile updated successfully");
        handleChangeStep();
        setIsLoading(false);
      } catch (error) {
        toast.error("Error updating profile. Please try again.");
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
  };

  // Get services that are in the user's serviceOffered array
  const offeredServices = services.filter(service =>
    user?.workerProfile?.servicesOffered?.includes(service?.id) // Fixed property name (was servicesOffered)
  );

  return (
    <main style={{ maxWidth: "1013px", margin: "auto" }}>
      <form className="m-auto" onSubmit={handleSubmit}>
        <div style={{ padding: "30px"}}>
          {/* Profile IMG & Fields */}
          <div className="d-flex flex-column align-items-center mb-5 gap-5">
            {/* Profile IMG */}
            <div className="col-4 d-flex justify-content-center">
              <label htmlFor="registerWorkerImg" style={{ cursor: "pointer" }}>
                {imgPreview ? (
                  <img
                    src={imgPreview}
                    alt=""
                    className="sm:w-36 sm:h-36 w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <AvatarPlaceholder className="rounded-full sm:w-36 sm:h-36 w-24 h-24" />
                )}
              </label>
              <input
                id="registerWorkerImg"
                type="file"
                accept="image/*"
                onChange={handleImgPreview}
                name="avatar"
                className="d-none"
              />
            </div>
            {/* END Profile IMG */}
          </div>
          {/* Fields */}
          <div className="sm:w-75 w-full">
            <div>
              <label className="fw-bold" htmlFor="hourlyPay">
                {t('hourlyRate')}
              </label>
            </div>
            <div className="input-group mt-2 sm:w-75 w-full">
              <input
                id="hourlyPay"
                name="hourlyPay"
                placeholder={t('hourlyRate')}
                className="form-control"
                type="number"
                min={0}
                step="1"
                aria-describedby="hourlyPay"
                aria-label="$25"
                defaultValue={workerProfiles?.hourlyPay}
                onKeyDown={handleNumberInput}
                onWheel={(e) => e.currentTarget.blur()}
              />
              <span className="input-group-text" id="hourlyPay">
                USD
              </span>
            </div>


            {/*  */}
            <div className="mt-4">
              <label className="fw-bold" htmlFor="location">
                {t('city')}<span style={{ color: "red" }}> *</span>
              </label>
            </div>
            <div className="mt-2 sm:w-75 w-full">
              <GooglePlacesAutocomplete
                apiKey={googlePlacesApiKey}
                selectProps={{
                  value: googleLocation,
                  onChange: setGoogleLocation,
                  placeholder: t('city'),
                }}
                autocompletionRequest={{
                  componentRestrictions: { country: 'PR' }
                }}
              />
            </div>

            {/* Physical Address Section */}
            <div className="mt-4">
              <label className="fw-bold">{t('physicalAddress')} <span style={{ color: "red" }}>*</span></label>
              <div className="mt-2 sm:w-75 w-full">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={workerProfiles?.physicalAddress?.line1 || t('exampleAddress')}
                  value={physicalAddress.line1}
                  //  defaultValue={workerProfiles?.physicalAddress?.line1}
                  onChange={e => setPhysicalAddress({ ...physicalAddress, line1: e.target.value })}
                  required
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={workerProfiles?.physicalAddress?.line2 || t('streetOrHighwayNumber')}
                  value={physicalAddress.line2}
                  //   defaultValue={workerProfiles?.physicalAddress?.line2}
                  onChange={e => setPhysicalAddress({ ...physicalAddress, line2: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={workerProfiles?.physicalAddress?.zipCode || t('zipCode')}
                  value={physicalAddress.zipCode}
                  //  defaultValue={workerProfiles?.physicalAddress?.zipCode}
                  onChange={e => setPhysicalAddress({ ...physicalAddress, zipCode: e.target.value })}
                  required
                />
                <small className="text-gray-400">
                  {t('includeStreetOrHighway')}
                </small>
              </div>
            </div>

            {/* Postal Address Section */}
            <div className="mt-4">
              <label className="fw-bold">{t('postalAddress')} <span style={{ color: "red" }}>*</span></label>
              <div className="mt-2 sm:w-75 w-full">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={workerProfiles?.postalAddress || t('examplePOBox')}
                  value={postalAddress}
                  defaultValue={workerProfiles?.postalAddress}
                  onChange={e => setPostalAddress(e.target.value)}
                  required
                />
                <small className="text-gray-400">
                  {t('enterFullPostalAddress')}
                </small>
              </div>
            </div>
          </div>
          {/* END Fields */}
          {/* END Profile IMG & Fields */}

          {/* Service Display */}
          <div className="d-flex flex-column align-items-start mb-4 mt-4">
           {offeredServices.length > 0 ?  <label className="fw-bold mb-2">
              {t('servicesYouWouldProvide')}
            </label>: ''}
            <div className="d-flex gap-2 flex-wrap">
              {offeredServices.map(service => (
                <span
                  key={service.id}
                  className="badge bg-primary"
                  style={{ fontSize: "1rem" }}
                >
                  {service.name}
                </span>
              ))}
            </div>
          </div>
          {/* END Service Display */}

          {/* Skill Selectors */}
          <div className="d-flex flex-column align-items-start mb-4">
           {offeredServices.length > 0 ? <label className="fw-bold mb-2">
              {t('whatCanYouDo')} <span style={{ color: "red" }}> *</span>
            </label> : ''}

            <div className="d-flex gap-5 col-9 flex-wrap">
              {offeredServices.map(service => (
                <div
                  key={service.id}
                  className="w-100 p-2 mb-3"
                  style={{
                    background: "#fff",
                    border: "1px solid black",
                    borderRadius: "5px",
                    minWidth: "300px",
                    maxWidth: "400px",
                  }}
                >
                  <h5 className="fw-semibold">{service.name}</h5>
                  <div className="d-flex flex-column">
                    {service.skills.map((skill) => (
                      <div key={skill._id} className="form-check ps-5">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`skill-${skill._id}`}
                          checked={selectedSkills.includes(skill._id)}
                          onChange={() => handleSkillToggle(skill._id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`skill-${skill._id}`}
                        >
                          {skill.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* END Skill Selectors */}
        </div>

        <button
          className="d-block btn btn-primary my-3 w-25 sm:ms-auto mx-auto"
          style={{
            background: "var(--primary-color)",
            outline: "none",
            border: "none",
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div
              className="spinner-border"
              style={{ height: "15px", width: "15px" }}
              role="status"
            ></div>
          ) : (
            t('continue')
          )}
        </button>
      </form>
    </main>
  );
}
// 